/**
 * Leaf module: pure pricing constants + rounding, importable by both the
 * model catalog (convex/config/models/) and convex/config/credit_pricing.ts
 * without creating import cycles.
 */

export const MODEL_CATEGORY_VALUES = [
  'llm',
  'image',
  'video',
  'threeD',
  'voice',
  'research',
] as const;

export type ModelCategory = (typeof MODEL_CATEGORY_VALUES)[number];

export const DEFAULT_CATEGORY_CREDIT_MULTIPLIERS: Record<
  ModelCategory,
  number
> = {
  llm: 1.25,
  image: 4,
  video: 5,
  threeD: 5,
  voice: 4,
  research: 10,
};

/**
 * Research output classes (docs/architecture/research-bucket-credit-pricing.md).
 * The delivered operation contract selects the class, never the provider name:
 * raw responses (search results, page text, records, metrics, rows) are 5x
 * company cost; a purchased provider-generated research report is 10x. IMAI
 * synthesis written from raw data uses ordinary agent pricing. These are
 * product pricing decisions, not provider fees.
 */
export const RESEARCH_PRICING_TIERS = [
  'raw_data',
  'generated_research_report',
] as const;
export type ResearchPricingTier = (typeof RESEARCH_PRICING_TIERS)[number];
export const RESEARCH_TIER_CREDIT_MULTIPLIERS: Record<
  ResearchPricingTier,
  number
> = {
  raw_data: 5,
  generated_research_report: 10,
};
/**
 * Tier labels pinned by quotes accepted before the output-class policy. They
 * are read for audit only; settlement always uses each quote's pinned numbers.
 */
export const LEGACY_RESEARCH_TIER_CREDIT_MULTIPLIERS = {
  standard_web_retrieval: 1.25,
  specialized_research: 10,
} as const;
/**
 * Audit label stored with every pinned research quote. New quotes after the
 * deployment that carries this version use the output-class policy; accepted
 * quotes, holds and in-flight attempts keep their pinned numbers.
 */
export const RESEARCH_CREDIT_POLICY_VERSION =
  'research-credit-policy-2026-09-27';

/** Applied when a charge site cannot name its category. */
export const DEFAULT_CREDIT_MULTIPLIER = 5;

/** 1 credit == 1 USD of CTC before the multiplier. */
export const CREDITS_PER_USD = 1;

/** Total significant digits kept on any credit charge (always rounded up). */
export const CREDIT_CHARGE_SIGNIFICANT_DIGITS = 4;

/** Balances below this floor are retained for accounting but are not usable. */
export const MIN_USABLE_CREDIT_BALANCE = 0.1;
/** Minimum balance before a realtime voice call may start (server + client gate). */
export const REALTIME_VOICE_MIN_START_CREDITS = 10;

export const getUsableCreditBalance = (balance: number): number => {
  if (!Number.isFinite(balance) || balance < MIN_USABLE_CREDIT_BALANCE) {
    return 0;
  }
  return balance;
};

export function isModelCategory(value: unknown): value is ModelCategory {
  return (
    typeof value === 'string' &&
    MODEL_CATEGORY_VALUES.includes(value as ModelCategory)
  );
}

/**
 * Rounds a credit amount UP so it keeps at most `digits` significant digits
 * in total. Ceiling keeps us from ever charging below ctc × multiplier.
 *   0.0123456 -> 0.01235
 *   1.23456   -> 1.235
 *   123.456   -> 123.5
 *   12345.6   -> 12350
 */
export function ceilCreditsToSignificantDigits(
  value: number,
  digits: number = CREDIT_CHARGE_SIGNIFICANT_DIGITS
): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  const safeDigits = Math.max(1, Math.min(15, Math.floor(digits)));
  const magnitude = Math.floor(Math.log10(value));
  const scale = 10 ** (safeDigits - 1 - magnitude);
  const scaled = value * scale;
  // EPSILON guard so exact representable values don't round up a full step.
  const ceiled = Math.ceil(scaled - Number.EPSILON * Math.abs(scaled));
  return ceiled / scale;
}
